// Lib
import { TWITTER } from '@/lib/socialProfile';
import { getPostType, TW_POST_TYPES } from '@/lib/posts/postTypes';
import type { TWPostTypesType } from '@/lib/posts/postTypes';
import { LiPostIcons } from '@/lib/albums/albumCardIconType';

// Hooks
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';

// Components
import Post from '@/components/base/Post';
import { AlbumPostIcon } from '@/components/pages/albums/AlbumFunnelModal/ModalSteps/ManualStep/components/post/AlbumPostIcon';
import { RetweetBadge } from '@/components/composite/Post/Twitter/RetweetBadge';
import { QuoteBadge } from '@/components/composite/Post/Twitter/QuoteBadge';
import { ReplyBadge } from '@/components/composite/Post/Twitter/ReplyBadge';

type TwitterAlbumPostProps = {
    data: {
        initial_tweet: {
            caption_formatted?: string;
            tags: { link: string }[];
        };
        type: TWPostTypesType;
        initial_author: {
            permalink: string;
            tag: string;
        };
        sub_type: TWPostTypesType;
        caption_formatted?: string;
        tags: { link: string }[];
        medias: {
            image?: string;
            thumbnail: string;
            poll?: { options: { label: string; percentage?: number }[] };
        }[];
    };
    shouldDisplayIcon: boolean;
};

const TwitterAlbumPost = ({ data, shouldDisplayIcon }: TwitterAlbumPostProps) => {
    const { OffsetReplace } = useTransformStringOnOffset();

    const type = getPostType({
        network: TWITTER,
        type: data?.sub_type,
        mediasLength: data?.medias?.length
    });

    // return initial tweet formatted caption when retweet
    const caption =
        data?.initial_tweet && data?.type === TW_POST_TYPES.TYPE_RETWEET
            ? data?.initial_tweet?.caption_formatted
            : data?.caption_formatted;

    // return initial tags when retweet
    const tags =
        data?.initial_tweet && data?.type === TW_POST_TYPES.TYPE_RETWEET
            ? data?.initial_tweet?.tags
            : data?.tags;

    return (
        <Post data={data} className="w-full">
            <Post.Content className="relative w-full text-left text-sm">
                <AlbumPostIcon
                    show={type && type !== TW_POST_TYPES.TYPE_STATUS && shouldDisplayIcon}
                    type={LiPostIcons[type]}
                />
                <Post.Status
                    remoteMedia
                    withMedia={
                        type !== TW_POST_TYPES.TYPE_STATUS &&
                        type !== TW_POST_TYPES.TYPE_LINK &&
                        type !== TW_POST_TYPES.TYPE_POLL
                    }
                    formattedCaption={
                        <>
                            {data?.initial_author &&
                            (data?.type === TW_POST_TYPES.TYPE_RETWEET ||
                                data?.type === TW_POST_TYPES.TYPE_QUOTE ||
                                data?.type === TW_POST_TYPES.TYPE_REPLY) ? (
                                <div className="mb-5 flex flex-wrap items-center gap-2 text-xs">
                                    {data?.type === TW_POST_TYPES.TYPE_RETWEET ? (
                                        <RetweetBadge className="text-xxs" />
                                    ) : null}
                                    {data?.type === TW_POST_TYPES.TYPE_QUOTE ? (
                                        <QuoteBadge className="text-xxs" />
                                    ) : null}
                                    {data?.type === TW_POST_TYPES.TYPE_REPLY ? (
                                        <ReplyBadge className="text-xxs" />
                                    ) : null}
                                    {data?.initial_author?.tag ? (
                                        <span className="text-icoBlue">
                                            {data?.initial_author?.tag}
                                        </span>
                                    ) : null}
                                </div>
                            ) : null}
                            <span className="break-words text-xs">
                                <OffsetReplace
                                    text={caption}
                                    components={Object.assign(
                                        {
                                            a: <span className="text-icoBlue" />,
                                            br: <br />
                                        },
                                        tags?.map((tag, id) => (
                                            <span key={id} className="text-icoBlue" />
                                        ))
                                    )}
                                />
                            </span>
                            {type === TW_POST_TYPES.TYPE_POLL && data?.medias[0]?.poll?.options ? (
                                <div className="mt-5 flex flex-col space-y-3 text-xs">
                                    {data?.medias[0]?.poll?.options?.map((option, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center justify-between">
                                            <span>{option?.label}</span>
                                            {(option?.percentage || option?.percentage === 0) && (
                                                <span>{option?.percentage}%</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </>
                    }
                />
            </Post.Content>
        </Post>
    );
};

export default TwitterAlbumPost;
